import { getAwsTranscribeCredentials } from '@pique/core/utils/aws-credentials';
import {
  removeAvatarMutation,
  updateUserProfileMutation,
  uploadAvatarMutation,
} from '@pique/graphql';
import type { GlobalStateService } from '@toeverything/infra';
import { Store } from '@toeverything/infra';

import type { AuthSessionInfo } from '../entities/session';
import type { FetchService } from '../services/fetch';
import type { GraphQLService } from '../services/graphql';

export interface AccountProfile {
  id: string;
  email: string;
  name: string;
  hasPassword: boolean;
  avatarUrl: string | null;
  emailVerified: string | null;
  isAdmin?: boolean;
  activeWorkspaceId?: string | null;
  featureFlags?: string[];
}

export class AuthStore extends Store {
  // One-time prefetch guard to avoid redundant credential fetches
  private hasPrefetchedSttKeys = false;
  constructor(
    private readonly fetchService: FetchService,
    private readonly gqlService: GraphQLService,
    private readonly globalStateService: GlobalStateService
  ) {
    super();
  }

  watchCachedAuthSession() {
    return this.globalStateService.globalState.watch<AuthSessionInfo>(
      'affine-cloud-auth'
    );
  }

  setCachedAuthSession(session: AuthSessionInfo | null) {
    this.globalStateService.globalState.set('affine-cloud-auth', session);
  }

  async fetchSession() {
    const url = `/api/auth/session`;
    // DO NOT SET CREDENTIALS INCLUDE IN TAURI
    const options: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
      },
      ...(environment.isTauri
        ? {}
        : { credentials: 'include' as RequestCredentials }),
    };

    const res = await this.fetchService.fetch(url, options);
    const data = (await res.json()) as {
      user?: AccountProfile | null;
    };

    if (!res.ok) {
      throw new Error('Get session fetch error: ' + JSON.stringify(data));
    }

    if (environment.isTauri && data.user && !this.hasPrefetchedSttKeys) {
      this.hasPrefetchedSttKeys = true;
      getAwsTranscribeCredentials(this.fetchService).catch(() => {});
    }
    return data;
  }

  async uploadAvatar(file: File) {
    await this.gqlService.gql({
      query: uploadAvatarMutation,
      variables: {
        avatar: file,
      },
    });
  }

  async removeAvatar() {
    await this.gqlService.gql({
      query: removeAvatarMutation,
    });
  }

  async updateLabel(label: string) {
    await this.gqlService.gql({
      query: updateUserProfileMutation,
      variables: {
        input: {
          name: label,
        },
      },
    });
  }
}
